import { useDispatch } from 'react-redux'

import type { RetailOrderLineItem } from '~/api/graphql/generated/types'
import { getIsRetailOrderLineItem } from '~/components/dashboard/invoices/invoiceUtils'
import {
  editChargeSheetProducerItemBatch,
  editChargeSheetRetailOrderLineItemProducerBatch,
} from '~/store/duck/clientFinanceData'
import {
  updateInvoiceLineItemProducerBatch,
  updateRetailOrderLineItemProducerBatch,
} from '~/store/reducers/invoiceV3'
import type { InvoiceLineItem } from '~/types'

/**
 * Hook for handling producer changes on charge sheet items
 * Supports both InvoiceLineItem and RetailOrderLineItem
 * Supports both charge sheet and invoice contexts
 *
 * @param item - The line item to edit
 * @param invoiceId - Optional invoice ID for invoice context
 * @returns Handler function that can be called directly from onChange events
 */
export const useChargeItemProducerEdit = (
  item: InvoiceLineItem | RetailOrderLineItem,
  invoiceId?: string,
) => {
  const dispatch = useDispatch()
  const isRetailOrderLineItem = getIsRetailOrderLineItem(item)

  return (producerId: string) => {
    if (isRetailOrderLineItem) {
      if (invoiceId) {
        // Invoice context - retail order line item
        dispatch(
          updateRetailOrderLineItemProducerBatch([
            {
              id: item.id,
              producerId: producerId || '',
              expectedModification: item.modificationDate,
            },
          ]),
        )
      } else {
        // Charge sheet context - retail order line item
        dispatch(
          editChargeSheetRetailOrderLineItemProducerBatch([
            {
              id: item.id,
              producerId: producerId || '',
              expectedModification: item.modificationDate,
            },
          ]),
        )
      }
    } else if (invoiceId) {
      // Invoice line item - Invoice context
      dispatch(
        updateInvoiceLineItemProducerBatch([
          {
            id: item.id,
            expectedModification: item.modificationDate,
            producerId: producerId as string,
          },
        ]),
      )
    } else {
      // Charge sheet context - invoice line item
      dispatch(
        editChargeSheetProducerItemBatch({
          clientId: (item as InvoiceLineItem).clientId,
          updateItemProducerInput: [
            {
              producerId,
              id: item.id,
              expectedModification: item.modificationDate,
            },
          ],
        }),
      )
    }
  }
}
